<template>
  <div class="white-background">
    <v-row noGutters>
      <BaseSidebar
        :items="[]"
        :initialMini="true"
      />
    </v-row>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      ref="tableRemoteRef"
      :headers="tableHeaders"
      :sortableColumns="['client', 'clientnumber', 'city','maincontact']"
      :items="displayedItems"
      :pagination="pagination"
      :isSearchable="false"
      :isCustomIcon="true"
      :title="$t('clients.pageName')"
      :loading="loading"
      :iconHeader="hasPermission('clients.add') ? 'mdi-plus' : null"
      :itemActions="actions"
      startIconName="person_outline"
      isSelectable
      hideDefaultFooter
      :dataCy="dataCy.clients.list"
      @search="debouncedSendQueryParams"
      @iconAction="onClickAction"
      @send-item="onClickRedirect"
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('sites.placeholder.searchBy')"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
        <v-switch
          v-model="queryParams.includeInactive"
          :label="$t('clients.labels.switchActiveInactive')"
        />
      </template>
      <template v-slot:item.client="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.client }}
        </div>
        <div v-else>
          {{ item.client }}
        </div>
      </template>

      <template v-slot:item.clientNumber="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.clientNumber }}
        </div>
        <div v-else>
          {{ item.clientNumber }}
        </div>
      </template>
      <template v-slot:item.mainContact="{item}">
        <div v-if="!!item.mainContact">
          {{ `${item.mainContact.firstName} ${item.mainContact.lastName}` }}
        </div>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ClientForm from '@/components/forms/ClientForm.vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import BaseSidebar from '@/components/shared/navigation/BaseSidebar.vue';
import TourGuideMixin from '@/mixins/TourGuideMixin';
import { debounce } from 'lodash';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import { clients } from '@/aq-data-cy.json';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import DeleteForm from '@/components/shared/widgets/DeleteForm.vue';

export default {
    components: {
        TableRemote,
        BaseSidebar,
        SearchWithFilters,
    },
    mixins: [TourGuideMixin],
    data() {
        return {
            loading: false,
            isInitializing: false,
            items: [],
            pagination: {},
            filterName: 'clients',
            queryParams: { perpage: 25, page: 1 },
            isActif: false,
            debouncedSendQueryParams: debounce(this.fetchQuery, 500),
            isFetching: false,
            allHeadersTitles: [
                'client',
                'clientNumber',
                'accessGroups',
                'city',
                'mainContact',
            ],
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'isLoggedIn',
            'tutorials',
            'userLang',
            'teamMainLang',
        ]),
        tableHeaders() {
            const headers = this.allHeadersTitles.map(header => ({
                text: this.$t(`clients.labels.${header}`),
                value: header,
                sortable: header !== 'accessGroups',
            }));
            headers.unshift({
                text: '',
                value: 'icon',
                sortable: false,
            });
            return headers;
        },
        displayedItems() {
            return this.items.map(item => ({
                ...item,
                client: item.name,
                clientNumber: item.accountNumber,
                accessGroups: item.accessGroups.map(accessGroup => accessGroup.name[this.userLang] || accessGroup.name[this.teamMainLang]),
            }));
        },
        dataCy() {
            return {
                clients,
            };
        },
        actions() {
            const actions = [];
            actions.push({
                name: this.$t('sites.active'),
                icon: 'edit',
                mode: 'edit',
                customValidationProperty: 'active',
                showOncustomValidationProperty: false,
            });
            actions.push({
                name: this.$t('sites.desactive'),
                icon: 'edit',
                mode: 'edit',
                customValidationProperty: 'active',
                showOncustomValidationProperty: true,
            });
            if (this.hasPermission('clients.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },

    },
    watch: {
        'queryParams.includeInactive': {
            handler() {
                this.getItems(1);
            },
            deep: true,
        },
        '$vuetify.breakpoint.width': function () {
            this.defineHeaders();
        },
    },
    async created() {
        this.isInitializing = true;
        await this.getItems();
        this.defineHeaders();
        this.isInitializing = false;
    },
    methods: {
        async getItems(nextPage = 1) {
            // TODO refactor this
            if (this.isFetching) return;
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.loading = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getClients(this.queryParams);
                this.items = [...data.clients];
                this.pagination = data.pagination;
                this.loading = false;
                this.isFetching = false;
                this.isInitializing = false;
                return;
            }
            if (nextPage <= this.queryParams.page) {
                this.isInitializing = false;
                return;
            }

            this.isFetching = true;
            this.loading = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getClients(this.queryParams);
            this.items = [...this.items, ...data.clients];
            this.pagination = data.pagination;
            this.loading = false;
            this.isFetching = false;
            this.isInitializing = false;
        },
        async fetchQuery({ order, ...params }) {
            this.loading = true;
            this.queryParams = {
                ...this.queryParams,
                ...params,
                order: this.prepareOrderParam(order),
            };
            await this.getItems();
            this.loading = false;
        },
        prepareOrderParam(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;

            switch (name) {
                case 'client':
                    newValue = 'name';
                    break;
                case 'clientnumber':
                    newValue = 'accountnumber';
                    break;
                case 'accessgroups':
                    newValue = 'accessgroups';
                    break;
                case 'city':
                    newValue = 'city';
                    break;
                case 'maincontact':
                    newValue = 'maincontact';
                    break;
                default:
                    return null;
            }

            return `${newValue}_${order}`;
        },
        async onClickAction(item, action) {
            if (item === 'mdi-plus') {
                this.$root.$emit('showModal', {
                    width: 750,
                    title: this.$t('clients.addClient'),
                    payload: { editAccessGroups: true },
                    component: Vue.component('ClientForm', ClientForm),
                    onConfirm: requestPayload => this.addClientRequest(requestPayload),
                });
            } else if (action.mode === 'delete') {
                await this.removeClient(item);
            } else if (action.mode === 'edit') {
                const actionName = !item.active ? 'activate' : 'deactivate';
                this.$root.$emit('showModal', {
                    title: this.$t(`clientPage.${actionName}`),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t(`clientPage.${actionName}Msg`) },
                    onConfirm: () => this.updateActive(item),
                    isForm: false,
                });
            }
        },
        onClickRedirect(item) {
            this.$router.push({
                name: 'sites',
                params: {
                    clientId: item.id,
                },
            });
        },
        async removeClient(item) {
            this.$root.$emit('showModal', {
                title: this.$t('clients.removeClient').toUpperCase(),
                component: Vue.component('DeleteForm', DeleteForm),
                buttonTitle: 'delete',
                payload: { msg: this.$t('clients.msg.remove'), security: this.$t('clients.msg.security') },
                isForm: true,
                onConfirm: async () => {
                    await this.$rq.removeClient(item.id);
                    this.$toasted.global.toast_success({
                        message: this.$t('clients.msg.clientDeleted', { data: item.name }),
                    });
                    await this.getItems();
                },
            });
        },
        async addClientRequest(requestPayload) {
            const { clientAccessGroups, ...res } = requestPayload;
            const payload = this.$utils.removeEmptyNullOrUndefined(res, true);

            const { data } = await this.$rq.addClient(payload);

            if (clientAccessGroups && clientAccessGroups.length) {
                await this.$rq.updateClientAccessGroup(data.id, { accessGroups: clientAccessGroups });
            }
            this.$toasted.global.toast_success({
                message: this.$t('msg.clientCreated', { data: data.name }),
            });
            await this.getItems();
        },
        async editClientRequest(requestPayload) {
            const { clientAccessGroups, ...res } = requestPayload;
            const payload = this.$utils.removeEmptyNullOrUndefined(res);
            const { id: clientId, active: status, initialStatus } = payload;
            // Clients can get updated if the client status is active
            if (initialStatus) {
                const { data } = await this.$rq.updateClientInfo(requestPayload.id, payload);
                if (clientAccessGroups && clientAccessGroups.length) {
                    await this.$rq.updateClientAccessGroup(data.id, { accessGroups: clientAccessGroups });
                }
                this.$toasted.global.toast_success({
                    message: this.$t('msg.clientModified', { data: data.name }),
                });
            }
            // Check if the current client status is diffrent from the initial status
            if (initialStatus !== status) {
                await this.$rq.updateClientStatus(clientId, { status });
                const { data: clientSites } = await this.$rq.getClientSites(clientId);
                if (this.hasPermission('sites.deactivate')) {
                    clientSites.sites.map(async site => { if (site.active !== status) { await this.$rq.updateSiteStatus(site.id, { status }); } });
                }
                this.$toasted.global.toast_success({
                    message: this.$t('msg.clientModified'),
                });
            }

            await this.getItems();
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
                page: 1,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.items = [];
            if (!this.isFetching) {
                this.getItems();
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getItems(this.queryParams.page + 1);
                // access "v-autocomplete" ref
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async updateActive(item) {
            /* Desactiver l'élément */
            await this.$rq.updateClientStatus(item.id, { status: !item.active });
            /* Faire un get pour raffraichir la liste  */
            this.queryParams.page = 1;
            await this.getItems(1);
        },
        defineHeaders() {
            this.allHeadersTitles = window.innerWidth > 800 ? [
                'client',
                'clientNumber',
                'accessGroups',
                'city',
                'mainContact',
            ] : [
                'client',
                'clientNumber',
                'city',
                'mainContact',
            ];
        },
    },
};
</script>
